import {
  BottomSheetWithIntent,
  CbhIcon,
  Divider,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { TimePicker } from "@src/appV2/redesign/components/TimePicker";
import { isAfter, isBefore } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import { validateClockOutTimestamp } from "../../MissedPunches/utils/validateClockOutTimestamp";
import { getBottomSheetIntent } from "./utils/getBottomSheetIntent";
import { inferDateForAmbiguousClockOutTime } from "./utils/inferDateForAmbiguousClockOutTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditClockOutTimeFormValues {
  clockOutTime?: Date;
}

interface EditClockOutTimeBottomSheetProps {
  modalState: UseModalState;
  clockOutTime?: Date;
  workplaceTimezone: string;
  clockOutButtonTappedAt?: Date;
  breakEndTime?: Date;
  clockInTime?: Date;
  shiftEndAt?: Date;
  geofenceDepartureTime?: Date;
  workerClockOut?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isDirectIntegrationFacility?: boolean;
  isMissedPunchRequestEnabled: boolean;
  onConfirm: (clockOutTime: Date) => void;
  onCancel?: () => void;
}

export function EditClockOutTimeBottomSheet(props: EditClockOutTimeBottomSheetProps) {
  const {
    modalState,
    clockOutTime,
    workplaceTimezone,
    clockOutButtonTappedAt,
    breakEndTime,
    clockInTime,
    shiftEndAt,
    geofenceDepartureTime,
    workerClockOut,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isDirectIntegrationFacility = false,
    isMissedPunchRequestEnabled,
    onConfirm,
    onCancel,
  } = props;

  const [saveAttempted, setSaveAttempted] = useState(false);
  const [hasUserChangedTime, setHasUserChangedTime] = useState(false);

  const { setValue, watch, reset } = useForm<EditClockOutTimeFormValues>({
    defaultValues: {
      clockOutTime: isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined,
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      const resetValue = isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined;
      reset({
        clockOutTime: resetValue,
      });
      setSaveAttempted(false);
      setHasUserChangedTime(false);
    }
  }, [modalState.modalIsOpen, clockOutTime, reset, workplaceTimezone]);

  const currentLocalClockOutTime = watch("clockOutTime");

  // Convert back to UTC for validation and submission
  const currentClockOutTime = isDefined(currentLocalClockOutTime)
    ? getUtcTimeFromPicker(currentLocalClockOutTime, workplaceTimezone)
    : undefined;

  const getValidationMessages = (
    selectedTime?: Date
  ): { errorMessage?: string; warningMessage?: string } => {
    if (!isDefined(selectedTime)) {
      return {};
    }

    // Apply inference to resolve ambiguous times for overnight shifts before validation
    const timeToValidate = hasUserChangedTime
      ? inferDateForAmbiguousClockOutTime(selectedTime, clockOutTime ?? selectedTime, {
          earliestAllowed: clockInTime,
          latestAllowed: clockOutButtonTappedAt,
        })
      : selectedTime;

    // Use centralized validation when feature flag is enabled
    if (isMissedPunchRequestEnabled && isDefined(clockInTime) && isDefined(shiftEndAt)) {
      const validationResult = validateClockOutTimestamp({
        selectedTime: timeToValidate,
        clockInTime,
        geofenceDepartureTime,
        shiftEndAt,
        workerClockOut,
        isGeofenceFacility,
        isNfcOrMeshFacility,
        isDirectIntegrationFacility,
      });

      // Return error message if validation failed
      if (!validationResult.isValid) {
        return { errorMessage: validationResult.message };
      }

      // Return warning message if there's a missed punch warning
      if (validationResult.warningType === "MISSED_PUNCH_WARNING") {
        return { warningMessage: validationResult.message };
      }

      return {};
    }

    // Legacy validation (when feature flag is off)
    // 1. If HCP attempts to edit clock out to after they tapped the clock out button
    if (isDefined(clockOutButtonTappedAt) && isAfter(timeToValidate, clockOutButtonTappedAt)) {
      return {
        errorMessage: `Please select a clock-out time at or before ${formatTimeWithTimezone(
          clockOutButtonTappedAt,
          workplaceTimezone,
          false
        )} — the time you tapped 'Clock Out'`,
      };
    }

    // 2. Clock out time is before break end
    if (isDefined(breakEndTime) && isBefore(timeToValidate, breakEndTime)) {
      return { errorMessage: "You cannot clock-out before your break ends." };
    }

    // 3. Clock out time is before or equal to clock in time
    if (isDefined(clockInTime) && !isAfter(timeToValidate, clockInTime)) {
      return { errorMessage: "You cannot clock-out before or at your clock-in time." };
    }

    return {};
  };

  const { errorMessage, warningMessage } = getValidationMessages(currentClockOutTime);

  const handleConfirm = () => {
    setSaveAttempted(true);

    if (isDefined(currentClockOutTime) && !errorMessage) {
      // Apply inference only if user changed the time
      const timeToSave = hasUserChangedTime
        ? inferDateForAmbiguousClockOutTime(
            currentClockOutTime,
            clockOutTime ?? currentClockOutTime,
            {
              earliestAllowed: clockInTime,
              latestAllowed: clockOutButtonTappedAt,
            }
          )
        : currentClockOutTime;

      onConfirm(timeToSave);
      modalState.closeModal();
    }
  };

  const handleCancel = () => {
    onCancel?.();
    modalState.closeModal();
  };

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      setValue("clockOutTime", newTime, { shouldDirty: true });
      setSaveAttempted(false);
      setHasUserChangedTime(true);
    }
  };

  const isValid = isDefined(currentClockOutTime);

  const { intent, intentContent, intentIcon } = getBottomSheetIntent({
    saveAttempted,
    errorMessage,
    warningMessage,
  });

  return (
    <BottomSheetWithIntent
      modalState={modalState}
      intent={intent}
      intentContent={intentContent}
      intentIcon={intentIcon}
      footer={
        <DialogFooter orientation="vertical" onClose={handleCancel}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Save
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={6}>
          <Title semibold variant="h4" component="h2">
            Edit time
          </Title>

          <Stack spacing={4}>
            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="clock" />
              <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
                Clock-out
              </Text>
              <Box textAlign="right">
                <Button aria-label="Select clock-out time" variant="outlined" size="small">
                  {isDefined(currentClockOutTime)
                    ? formatTimeWithTimezone(currentClockOutTime, workplaceTimezone, false)
                    : "--:-- --"}
                </Button>
              </Box>
            </Stack>

            <Divider />
            <TimePicker
              ampm
              skipDisabled
              preventParentTouchStart
              value={currentLocalClockOutTime}
              timeSteps={{ minutes: 1 }}
              containerSx={{
                // make sure the time picker takes as little horizontal space as possible
                // this preserves more area on the left/right for the bottom sheet to capture touch events
                // for swipe-down closing (dismiss)
                alignSelf: "center",
              }}
              sx={{
                justifyContent: "center",
                "& .MuiList-root": {
                  scrollbarWidth: "none",
                },
              }}
              onChange={handleTimeChange}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
